<template>
  <div class="cutsheet__container">

    <div 
      class="container py-5" 
      v-if="!loading && !cutSheet" >
      <article 
        class="message is-warning is-fullwidth">
        <div class="message-body">
          This cut sheet does not exist.
        </div>
      </article>
    </div>

    <template v-if="cutSheet">
      <div class="cutsheet__info">

        <div class="field">
          <label class="label">Created On</label>
          <div>
            {{ formatDate(parse(cutSheet.created_on)) }}
          </div>
        </div>

        <div class="field">
          <label class="label">Created By</label>
          <div>
            {{ cutSheet.created_user.display_name }}
          </div>
        </div>

        <hr class="my-3"/>

        <div class="field">
          <label class="label">Project</label>
          <div v-if="cutSheet.project">
            {{ cutSheet.project.project_name }}
          </div>
        </div>

        <div class="field">
          <label class="label">Cut Sheet No.</label>
          <v-text-field 
            disabled
            placeholder="Enter cut sheet number"
            v-model="cutSheet.cutsheet_no"
            @blur="change('cutsheet_no', cutSheet.cutsheet_no)">
          </v-text-field>
        </div>

        <div class="field">
          <label class="label">Tag</label>
          <v-text-field 
            placeholder="Enter tag"
            v-model="cutSheet.item"
            @blur="change('item', cutSheet.item)">
          </v-text-field>
        </div>

        <div class="field">
          <label class="label">Expected Delivery Date*</label>
          <v-datepicker
            required
            :value="parse(cutSheet.expected_delivery_date)"
            @input="change('expected_delivery_date', $event)">
          </v-datepicker>
        </div>

        <div class="field">
          <label class="label mb-0">
            Product
          </label>
          <div> 
            {{ cutSheet.input_product_master.name }}
          </div>
          <div class="help">
            Thickness: {{ cutSheet.thick_in_str }} inches
          </div>
        </div>

        <a @click="managingSteps = true">Manage Cut Sheet Steps</a>

        <a 
          class="button is-outlined is-fullwidth mt-5"
          @click="launchConfirmDeleteCutSheet()">
          <span class="icon is-medium">
            <i class="fas fa-trash has-text-danger"></i>
          </span>
          <span>Delete Cut Sheet</span>
        </a>

      </div>

      <div class="cutsheet__parts" v-if="cutSheet">

        <div class="level cutsheet__controls">
          <div class="level-left buttons mb-0">
            <a class="button is-primary" @click="addingPart = true">
              Add A Part
            </a>
          </div>
          <div class="level-right buttons mb-0">
            <a class="button" 
              v-if="itemsSelected.length > 0"
              @click="launchConfirmDeleteParts()">
              <span class="icon is-medium">
                <i class="fas fa-trash has-text-danger"></i>
              </span>
              <span>Delete {{ itemsSelected.length }} Part<span v-if="itemsSelected.length > 0">s</span></span>
            </a>
            <!-- <a class="button" 
              :disabled="itemsSelected.length == 0"
              @click="assigningAssembly = true">
              <span class="icon is-medium">
                <i class="fas fa-paperclip"></i>
              </span>
              <span>Assign to Assembly Sheet</span>
            </a>
            <a class="button" 
              :disabled="itemsSelected.length == 0"
              @click="assigningSkid = true">
              <span class="icon is-medium">
                <i class="fas fa-list"></i>
              </span>
              <span>Assign to Skid</span>
            </a> -->
          </div>
        </div>

        <table class="table is-hoverable is-fullwidth sticky-header has-smallish-font">

          <thead>
            <tr class="has-text-centered">
              <th class="part__selector">
                <label class="checkbox" v-if="ids.length > 0">
                  <input
                    type="checkbox"
                    v-model="allSelected">
                </label>
              </th>
              <th 
                v-for="(col, i) in colTemplate"
                :class="col.cellClass" 
                :key="col.label + '-' + i">
                {{ col.label }}
              </th>
              <th 
                class="is-capitalized"
                v-for="step in cutSheetSteps" 
                :key="step.id">
                {{ step.name }}
              </th>
              <th></th>
            </tr>
          </thead>

          <tbody v-if="cutSheet.parts.length > 0">

            <tr
              class="v-centered has-text-centered" 
              v-for="(row, rowIndex) in cutSheet.parts" :key="rowIndex">

              <td class="part__selector">
                <label class="checkbox">
                  <input
                    type="checkbox"
                    :checked="itemsSelected.indexOf(row.id) != -1"
                    @input="selectItem(row.id)">
                </label>
              </td>

              <template v-if="row.id == editingPart">
                <td 
                  v-for="(col, colIndex) in colTemplate"
                  :class="col.cellClass" 
                  :key="colIndex">
                  <div class="centered" v-if="col.component">
                    <component 
                      :is="col.component"
                      v-bind="col.bind"
                      :value="row[col.field]"
                      @input="col.component == 'VCheckbox' ? 
                        updatePart(row.id, col.field, $event) 
                        : updatePartAttachment(col.field, row.id, $event)"
                      @blur="updatePart(row.id, col.field, $event)"/>
                  </div>
                  <span v-else>{{ row[col.field] }}</span>
                </td>
              </template>

              <template v-else>
                <td 
                  v-for="(col, colIndex) in colTemplate" 
                  :class="col.cellClass" 
                  :key="colIndex">
                  <div
                    class="text--nowrap" 
                    v-if="col.field != 'attachment' && col.component != 'VCheckbox'">
                    {{ row[col.field] }}
                  </div>
                  <!-- if attachment, show link to attachment -->
                  <a 
                    v-if="row[col.field] && col.field == 'attachment'" 
                    :href="row[col.field]" 
                    _target="blank">
                    attachment
                  </a>
                  <v-checkbox 
                    v-if="col.component == 'VCheckbox'"
                    disabled
                    :value="row.requires_assembly">
                  </v-checkbox>
                </td>
              </template>

              <!-- cut sheet steps -->
              <td 
                v-for="step in cutSheetSteps" 
                :key="step.name + '-' + step.id">
                <v-checkbox 
                  :disabled="row.id != editingPart"
                  :value="!!row.steps[step.id]"
                  @input="changeStep(row, step, $event)">
                </v-checkbox>
              </td>

              <!-- For assembly, show link to assembly -->
              <!-- <td>
                <router-link 
                  :to="{ 
                    name: 'AssemblyView', 
                    params: { 
                      assemblySheetId: row.assembly_detail.assemblysheet_id 
                    }
                  }">
                  <a v-if="row.assembly_detail.assemblysheet_id != 0">
                    Assembly Details
                  </a>
                </router-link>
              </td> -->

              <!-- For skid, show link to skid -->
              <!-- <td>
                <router-link 
                  :to="{ name: 'SkidView', params: { skidId: row.skid.id }}">
                  {{ row.skid.skid_no }}
                </router-link>
              </td> -->

              <!-- cut sheet actions -->
              <td>
                <div class="buttons flex flex--nowrap buttons-right">
                  <template v-if="row.id == editingPart">
                    <a
                      v-if="rowIndex != 0"
                      @click="moveUp(row.id, rowIndex, row.sort_ix)"
                      class="button is-small">
                      <span class="icon is-medium">
                        <i class="fas fa-chevron-up has-text-grey">
                        </i>
                      </span>
                    </a>
                    <a 
                      v-if="rowIndex + 1 != cutSheet.parts.length"
                      @click="moveDown(row.id, rowIndex, row.sort_ix)"
                      class="button is-small">
                      <span class="icon is-medium">
                        <i class="fas fa-chevron-down has-text-grey">
                        </i>
                      </span>
                    </a>
                  </template>
                  <a
                    @click="editPart(row.id)"
                    class="button is-small">
                    <span class="icon is-medium">
                      <i class="fas fa-edit has-text-grey">
                      </i>
                    </span>
                  </a>
                  <a
                    @click="copyPart(row)"
                    class="button is-small">
                    <span class="icon is-medium">
                      <i class="fas fa-copy has-text-grey">
                      </i>
                    </span>
                  </a>
                  <a
                    class="button is-small"
                    @click="launchConfirmDeleteParts(row.id)">
                    <span class="icon is-medium">
                      <i class="fas fa-trash has-text-grey">
                      </i>
                    </span>
                  </a>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <manage-steps-modal v-model="managingSteps"/>

      <v-confirm-modal 
        :title="confirmDelete.title"
        :message="confirmDelete.message"
        :type="confirmDelete.type"
        v-model="confirmDelete.state"
        @cancel="confirmDelete.state = false"
        @confirm="deleteConfirmedCb ? deleteConfirmedCb() : null">
      </v-confirm-modal>

      <assign-skid-modal
        v-if="cutSheet.project"
        :skids="projectSkids"
        :parts="itemsSelected"
        :project-id="cutSheet.project.id"
        v-model="assigningSkid">
      </assign-skid-modal>

      <assign-assembly-modal
        v-if="cutSheet.project"
        :assembly-sheets="projectAssemblySheets"
        :parts="selectedPartObjects"
        :project-id="cutSheet.project.id"
        v-model="assigningAssembly">
      </assign-assembly-modal>

      <copy-part-modal
        v-model="copyingPart"
        :part-number.sync="copiedPart.part_no"
        @add-part="addCopiedPart()">
      </copy-part-modal>

      <add-part-modal
        v-model="addingPart"
        :steps="cutSheetSteps"
        :cut-sheet-id="cutSheet.id"
        @success="addPartSuccess"/>

    </template>

    <v-loading :value="loading" message="Loading Cut Sheet ..."/>

  </div>
</template>

<script>
import VTextField from '@/components/base/VTextField'
import VCheckbox from '@/components/base/VCheckbox'
import VContentEditable from '@/components/base/VContentEditable'
import VSelect from '@/components/base/VSelect'
import VFileUpload from '@/components/base/VFileUpload'
import VConfirmModal from '@/components/base/VConfirmModal'
import VDatepicker from '@/components/base/VDatepicker'
import VLoading from '@/components/base/VLoading'

import ManageStepsModal from '@/components/cutsheet/ManageStepsModal'
import AssignSkidModal from '@/components/cutsheet/AssignSkidModal'
import AssignAssemblyModal from '@/components/cutsheet/AssignAssemblyModal'
import CopyPartModal from '@/components/cutsheet/CopyPartModal'
import AddPartModal from '@/components/cutsheet/AddPartModal'
import ProductSelector from '@/components/inventory/ProductSelector'

import { parseISO, format } from 'date-fns'

import { mapState, mapActions, mapMutations } from 'vuex'

///////////////////////////////////////////////////////////////////////////////////
// Before submit need to verify that the quantities and part numbers are filled out
///////////////////////////////////////////////////////////////////////////////////

export default {
  components: { 
    VTextField, VCheckbox, VSelect, VDatepicker, VContentEditable, VFileUpload,
    VConfirmModal, VLoading, AssignSkidModal, ManageStepsModal, AssignAssemblyModal,
    CopyPartModal, AddPartModal, ProductSelector
  },
  data() {
    return {
      colTemplate: [
        { label: 'Part #', field: 'part_no', component: 'VContentEditable', 
          default: null, cellClass: 'border--after', bind: { class: 'text--nowrap' } },
        { label: 'W"', field: 'width_in_str', component: 'VContentEditable', default: null, update: 'width_mm' },
        { label: 'L"', field: 'length_in_str', component: 'VContentEditable', default: null, update: 'length_mm' },
        { label: 'Quantity', field: 'quantity', component: 'VContentEditable', default: null, cellClass: 'border--before border--after' },
        { 
          label: 'Shop Ticket', field: 'attachment', component: 'VFileUpload', 
          default: { file: null, name: null, url: null }, 
          bind: { class: 'is-small', placeholder: 'choose file', 'file-type-validator': '.*' },
          cellClass: 'border--after' 
        },
        { 
          label: 'Assembly', field: 'requires_assembly', component: 'VCheckbox', 
          default: false, 
        }
      ],
      allSelected: false,
      itemsSelected: [],
      cutSheet: null,
      loading: false,
      managingSteps: false,
      managingProducts: false,
      editingPart: null,
      confirmDelete: {
        state: false,
        title: 'Delete Confirmation',
        message: 'Are you sure you want to delete this cut sheet?',
        type: 'danger'
      },
      deleteConfirmedCb: null,
      assigningSkid: false,
      assigningAssembly: false,
      copyingPart: false,
      copiedPart: {
        part_no: null
      },
      addingPart: false,
      parse: (d) => {
        return d ? parseISO(d) : null;
      },
      formatDate: (d) => {
        return d ? format(d, 'yyyy-MM-dd H:mm:ss') : d;
      }
    }
  },
  computed: {
    ...mapState("projects", ["projects"]),
    ...mapState("cutSheets", ["cutSheetSteps"]),
    ...mapState("skids", ["skids"]),
    ...mapState("assembly", ["assemblySheets"]),
    ids() {
      if (this.cutSheet.parts) {
        return this.cutSheet.parts.map(d => d.id);
      } else {
        return []
      }
    },
    cutSheetId() {
      return this.$route.params.cutSheetId;
    },
    projectSkids() {
      if (this.cutSheet.project) {
        return this.skids.filter(
          d => d.project_id == this.cutSheet.project.id
        );
      } else {
        return []
      }
    },
    projectAssemblySheets() {
      if (this.cutSheet.project) {
        return this.assemblySheets.filter(
          d => d.project_id == this.cutSheet.project.id
        );
      } else {
        return []
      }
    },
    selectedPartObjects() {
      return this.cutSheet.parts.filter(
        d => this.itemsSelected.indexOf(d.id) != -1
      );
    }
  },
  watch: {
    allSelected(state) {
      const { itemsSelected } = this;
      if (state) {
        this.ids.forEach(id => {
          const index = itemsSelected.indexOf(id);
          if (index == -1) {
            itemsSelected.push(id);
          }
        })
      } else {
        itemsSelected.splice(0, itemsSelected.length);
      }
    },
    cutSheetId() {
      if (this.$route.params.cutSheet) {
        this.cutSheet = this.$route.params.cutSheet;
      } else {
        this.getCutSheet();
      }
    }
  },
  created() {
    this.initialize();
  },
  methods: {
    ...mapActions("projects", ["getProjects"]),
    ...mapActions("cutSheets", ["getSteps"]),
    ...mapMutations("app", ["setNotification"]),
    ...mapActions("skids", ["getSkids"]),
    ...mapActions("assembly", ["getAssemblySheets"]),
    initialize() {
      // Get projects if there are no projects
      if (this.projects.length == 0) {
        this.getProjects({ per_page: 1000 });
      }
      // Get Skids if there are no skids
      if (this.skids.length == 0) {
        this.getSkids({ per_page: 1000 });
      }

      if (this.cutSheetSteps.length == 0) {
        this.getSteps();
      }

      if (this.$route.params.cutSheet) {
        this.cutSheet = this.$route.params.cutSheet;
        this.getAssemblySheets({ 
          project_id: this.cutSheet.project.id,
          per_page: 1000 
        });
      } else {
        this.getCutSheet();
      }
    },
    getCutSheet() {
      this.loading = true;
      this.$http.cutsheet.get(this.$route.params.cutSheetId)
        .then(resp => {
          if (resp.data.results.length > 0) {
            this.cutSheet = resp.data.results[0];
            this.getAssemblySheets({ 
              project_id: this.cutSheet.project.id,
              per_page: 1000 
            });
          }
          this.loading = false;
        }).catch(() => {
          this.loading = false;
        });
    },
    change(fieldName, v) {
      let params = {};
      params[fieldName] = v;

      this.$http.cutsheet.put(this.cutSheet.id, params);
    },
    selectItem(i) {
      let index = this.itemsSelected.indexOf(i);
      if (index == -1) {
        this.itemsSelected.push(i);
      } else {
        this.itemsSelected.splice(index, 1);
        if (this.itemsSelected.length == 0) {
          this.allSelected = false;
        }
      }
    },
    deleteSelected() {
      this.itemsSelected.forEach(item => {
        this.deletePart(item);

        this.cutSheet.parts.forEach((part, i) => {
          if (part.id == item) {
            this.cutSheet.splice(i, 1);
          }
        });
      });
      this.confirmDelete.state = false;
      this.itemsSelected.splice(0, this.itemsSelected.length);
    },  
    launchConfirmDeleteCutSheet() {
      this.deleteConfirmedCb = () => {
        this.deleteCutSheet();
      }
      this.confirmDelete.state = true;
      this.confirmDelete.message = 'Are you sure you want to delete this cut sheet?'
    },
    launchConfirmDeleteParts(partId) {
      if (partId) {
        this.deleteConfirmedCb = () => {
          this.deletePart(partId);
        }
        this.confirmDelete.message = 'Are you sure you want to delete \
          this cut sheet part? Assembly sheets and skids that use this \
          part will also be affected.'
        this.confirmDelete.state = true;
      } else {
        this.deleteConfirmedCb = () => {
          this.deleteSelected();
        }
        this.confirmDelete.message = `Are you sure you want to delete \
          the ${this.itemsSelected.length} selected parts? Assembly sheets \
          and skids that use this part will also be affected.`
        this.confirmDelete.state = true;
      }
    },
    deleteCutSheet() {
      this.$http.cutsheet.delete(this.cutSheet.id)
        .then(() => {
          this.confirmDelete.state = false;
          this.notifyDeleteSuccess();
          this.$router.push({ name: 'CutSheets' });
        });
    },
    notifyDeleteSuccess() {
      this.setNotification({
        type: 'success',
        status: true,
        message: 'Cut sheet successfully deleted'
      });
    },
    moveUp(partId, rowIndex, sort_ix) {
      let prev = this.cutSheet.parts[rowIndex - 1].sort_ix;
      this.updatePart(this.cutSheet.parts[rowIndex - 1].id, 'sort_ix', sort_ix).then(
        () => this.updatePart(partId, 'sort_ix', prev).then(
          () => this.sort()
        )
      )
    },
    moveDown(partId, rowIndex, sort_ix) {
      let next = this.cutSheet.parts[rowIndex + 1].sort_ix;
      this.updatePart(this.cutSheet.parts[rowIndex + 1].id, 'sort_ix', sort_ix).then(
        () => this.updatePart(partId, 'sort_ix', next).then(
          () => this.sort()
        )
      )
    },
    sort() {
      this.cutSheet.parts.sort((a, b) => a.sort_ix - b.sort_ix);
    },
    copyPart(part) {
      this.copiedPart = Object.assign({}, part);
      this.copyingPart = true;
    },
    addCopiedPart() {
      this.copyingPart = false;

      const { copiedPart } = this;
      let modifiedSteps = [];
      Object.keys(copiedPart.steps).forEach(stepId => {
        modifiedSteps.push({ id: stepId });
      });

      copiedPart.steps = modifiedSteps;
      delete copiedPart.assembly_detail;
      delete copiedPart.attachment;
      delete copiedPart.skid;
      delete copiedPart.assembly_no;
      delete copiedPart.created_on;
      delete copiedPart.skid_id;
      delete copiedPart.skid_no;
      this.$http.cutsheetParts.post(copiedPart, { cutsheet_id: this.cutSheet.id })
        .then(resp => this.addPartSuccess(resp))
    },
    addPartSuccess(resp) {
      this.cutSheet.parts.push(resp.data);
      this.editingPart = resp.data.id;
    },
    editPart(partId) {
      if (partId == this.editingPart) {
        this.editingPart = null;
      } else {
        this.editingPart = partId;
      }
    },
    updatePart(partId, field, value) {
      return new Promise((resolve, reject) => {
        let data = {};
        data[field] = value;
        this.$http.cutsheetPart.put(partId, data).then(resp => {
          this.cutSheet.parts.forEach((part, i) => {
            if (part.id == resp.data.id) {
              this.cutSheet.parts.splice(i, 1, resp.data);
              resolve(resp);
            }
          })
        }).catch(err => reject(err))
      })
    },
    deletePart(partId) {
      this.$http.cutsheetPart.delete(partId);
      let index = this.ids.indexOf(partId);
      this.cutSheet.parts.splice(index, 1);
      this.confirmDelete.state = false;
    },
    updatePartAttachment(field, partId, v) {
      if (field == 'attachment') {
        this.savePartAttachment(partId, v.file).then(resp => {
          this.cutSheet.parts.forEach((part, i) => {
            if (part.id == resp.data.id) {
              this.cutSheet.parts.splice(i, 1, resp.data);
            }
          })
        })
      }
    },
    savePartAttachment(partId, attachment) {
      return new Promise((resolve, reject) => {
        let formData = new FormData();
        formData.append('attachment', attachment, attachment.name);
        this.$http.cutsheetPart.addAttachment(partId, formData)
          .then(resp => {
            resolve(resp);
          }).catch(() => {
            reject();
          })
      })
    },
    changeStep(part, step, v) {
      if (v) {
        this.$http.cutsheetPartSteps.post({ 
          cutsheetpart_id: part.id, step_id: step.id 
        }, {
          cutsheetpart_id: part.id
        }).then(resp => {
          this.cutSheet.parts.forEach(cutSheetPart => {
            if (cutSheetPart.id == part.id) {
              cutSheetPart.steps[step.id] = resp.data.step;
            }
          })
        })
      } else {
        this.$http.cutsheetPartSteps.delete(part.steps[step.id].id)
          .then(() => {
            this.cutSheet.parts.forEach(cutSheetPart => {
              if (cutSheetPart.id == part.id) {
                delete cutSheetPart.steps[step.id];
              }
            })
          })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/table.scss';
@import '@/styles/flexbox.scss';

.editable-text {
  min-width: 30px;
}

.cutsheet {
  &__container {
    display: flex;
    flex-direction: row;
  }
  &__info {
    padding: 1.5em;
    width: 15%;
  }
  &__parts {
    padding-right: 1.5em;
    padding-top: 1.5em;
    padding-bottom: 1.5em;
    width: 85%;
  }
}

.label-and-button {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  .tag {
    cursor: pointer;
    &:hover {
      background: gainsboro;
    }
  }
}

@media print {
  .cutsheet__controls {
    display: none;
  }
  .part__selector {
    display: none;
  }
}

</style>
